import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '../components/states'
import { searchProducts, type ItemStock } from '../data/stock'
import { formatCurrency, formatIdentifier, formatQuantity } from '../lib/format'
import { PRICE_MISSING, SKU_LIST_PRICE_LABEL, STOCK_UNKNOWN } from '../lib/labels'
import './ProductsScreen.css'

/** Same cap-and-page pattern as Orders. */
const PAGE_SIZE = 20
/** Don't re-query on every keystroke. */
const SEARCH_DEBOUNCE_MS = 300

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; detail: string }
  | { status: 'ready'; items: ItemStock[]; totalCount: number }

/**
 * Route `/products` — search the ESL catalog (PIMBERLY items) by SKU
 * fragment or description word, then open the SKU page. Without this a rep
 * can only reach stock for items they have already ordered.
 *
 * Every row shows SKU, description, Available and ESL list price. The price
 * label is Jeff's exact literal (lib/labels.ts), once above the list rather
 * than repeated on every row; each row's price is still worded "list".
 */
export function ProductsScreen() {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  // A new search starts back at page 1.
  useEffect(() => {
    setLimit(PAGE_SIZE)
  }, [search])

  const load = useCallback(() => {
    setState({ status: 'loading' })
    void searchProducts(search, limit).then((result) => {
      if (result.status === 'error') {
        setState({ status: 'error', detail: result.detail })
        return
      }
      setState({ status: 'ready', items: result.items, totalCount: result.totalCount })
    })
  }, [search, limit])

  useEffect(() => {
    load()
  }, [load])

  const showMore = useCallback(() => setLimit((n) => n + PAGE_SIZE), [])

  return (
    <>
      <h1>Products</h1>
      <p className="esl-muted esl-products__intro">ESL catalog items, with the same Available number as the website.</p>

      <div className="esl-products__controls">
        <div className="esl-field">
          <label className="esl-label" htmlFor="esl-products-search">
            Search
          </label>
          <input
            id="esl-products-search"
            className="esl-input"
            type="search"
            inputMode="search"
            autoComplete="off"
            placeholder="SKU or description"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <p className="esl-products__price-label">{SKU_LIST_PRICE_LABEL}</p>

      {state.status === 'loading' ? (
        <div className="esl-products__state">
          <LoadingState label="Loading products…" />
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="esl-products__state">
          <ErrorState title="We could not load products" detail={state.detail} onRetry={load} />
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="esl-products__state">
          <EmptyState
            title={search ? 'No matching products' : 'No products'}
            detail={search ? `Nothing matches "${search}".` : undefined}
          />
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <>
          <p className="esl-muted esl-products__count">
            {state.totalCount === 1 ? '1 item' : `${state.totalCount.toLocaleString('en-US')} items`}
          </p>
          <ul className="esl-products__list">
            {state.items.map((item) => (
              <li key={item.itemId} className="esl-products__row">
                <Link to={`/sku/${encodeURIComponent(item.itemId)}`} className="esl-products__row-link">
                  <ProductCard item={item} />
                </Link>
              </li>
            ))}
          </ul>

          {state.totalCount - state.items.length > 0 ? (
            <button type="button" className="esl-btn esl-btn--secondary" onClick={showMore}>
              Show {Math.min(state.totalCount - state.items.length, PAGE_SIZE)} more
            </button>
          ) : null}
        </>
      ) : null}
    </>
  )
}

/** SKU · description · Available · ESL list price. Unknowns in words. */
function ProductCard({ item }: { item: ItemStock }) {
  return (
    <>
      <span className="esl-products__sku">{formatIdentifier(item.itemId)}</span>
      {item.itemDescription ? <p className="esl-products__desc">{item.itemDescription}</p> : null}
      <div className="esl-products__facts">
        <span>
          <span className="esl-products__fact-label">Available </span>
          <span className="esl-products__fact-value">
            {item.available !== null ? formatQuantity(item.available) : STOCK_UNKNOWN}
          </span>
        </span>
        <span>
          <span className="esl-products__fact-label">List </span>
          <span className="esl-products__fact-value">
            {item.listPrice !== null ? formatCurrency(item.listPrice) : PRICE_MISSING}
          </span>
        </span>
      </div>
    </>
  )
}
